import React, { useState } from 'react';
import { 
  Check, 
  X, 
  Star, 
  Shield, 
  Users, 
  Building, 
  Heart,
  Brain,
  Zap,
  Phone,
  Mail,
  HelpCircle
} from 'lucide-react';

interface PricingPlan {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: Array<{
    name: string;
    included: boolean;
    limit?: string;
  }>;
  popular?: boolean;
  cta: string;
  userType: 'individual' | 'provider' | 'enterprise';
}

const pricingPlans: PricingPlan[] = [
  {
    id: 'free',
    name: 'ZippyHealth Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for individuals starting their health journey',
    userType: 'individual',
    cta: 'Start Free',
    features: [
      { name: 'Track up to 3 health conditions', included: true },
      { name: 'Basic symptom logging', included: true },
      { name: 'Medication reminders', included: true },
      { name: 'Food diary (basic)', included: true },
      { name: 'Data export (monthly)', included: true, limit: '1/month' },
      { name: 'Mobile app access', included: true },
      { name: 'AI health insights', included: false },
      { name: 'Provider collaboration', included: false },
      { name: 'Advanced analytics', included: false },
      { name: 'Device integrations', included: false },
      { name: 'Priority support', included: false },
    ]
  },
  {
    id: 'pro',
    name: 'ZippyHealth Pro',
    price: '$9.99',
    period: 'per month',
    description: 'Advanced health tracking for serious health management',
    userType: 'individual',
    popular: true,
    cta: 'Start Pro Trial',
    features: [
      { name: 'Unlimited health conditions', included: true },
      { name: 'Advanced symptom tracking', included: true },
      { name: 'Smart medication management', included: true },
      { name: 'Enhanced food tracker with AI', included: true },
      { name: 'Unlimited data exports', included: true },
      { name: 'AI health insights & predictions', included: true },
      { name: 'Provider collaboration tools', included: true },
      { name: 'Advanced correlation analytics', included: true },
      { name: 'Device integrations (10+ devices)', included: true },
      { name: 'Email support', included: true },
      { name: 'Telehealth ready', included: false },
    ]
  },
  {
    id: 'premium',
    name: 'ZippyHealth Premium',
    price: '$19.99',
    period: 'per month',
    description: 'Complete health ecosystem with provider tools',
    userType: 'individual',
    cta: 'Start Premium',
    features: [
      { name: 'Everything in Pro', included: true },
      { name: 'Provider dashboard access', included: true },
      { name: 'Telehealth integration', included: true },
      { name: 'Lab result analysis', included: true },
      { name: 'Predictive health modeling', included: true },
      { name: 'Blockchain document security', included: true },
      { name: 'Priority phone support', included: true },
      { name: 'Custom health reports', included: true },
      { name: 'Family health management', included: true, limit: 'Up to 5 members' },
      { name: 'API access for integrations', included: true },
      { name: 'White-label options', included: false },
    ]
  },
  {
    id: 'provider',
    name: 'Healthcare Provider',
    price: '$50',
    period: 'per provider/month',
    description: 'Professional tools for healthcare providers',
    userType: 'provider',
    cta: 'Request Demo',
    features: [
      { name: 'Patient management dashboard', included: true },
      { name: 'HIPAA-compliant data handling', included: true },
      { name: 'Clinical decision support AI', included: true },
      { name: 'EHR integration capabilities', included: true },
      { name: 'Patient collaboration tools', included: true },
      { name: 'Advanced analytics & reporting', included: true },
      { name: 'Telehealth platform integration', included: true },
      { name: 'Lab result integration', included: true },
      { name: 'Unlimited patient connections', included: true },
      { name: '24/7 priority support', included: true },
      { name: 'Custom workflows', included: true },
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: 'contact us',
    description: 'Scalable solutions for health systems and organizations',
    userType: 'enterprise',
    cta: 'Contact Sales',
    features: [
      { name: 'Everything in Provider plan', included: true },
      { name: 'Multi-facility management', included: true },
      { name: 'Custom integrations', included: true },
      { name: 'Advanced security & compliance', included: true },
      { name: 'Dedicated account manager', included: true },
      { name: 'Custom analytics & reporting', included: true },
      { name: 'SLA guarantees', included: true },
      { name: 'On-premise deployment options', included: true },
      { name: 'Custom training & onboarding', included: true },
      { name: 'White-label solutions', included: true },
      { name: 'Population health analytics', included: true },
    ]
  }
];

const faqs = [
  {
    question: "Is my health data secure?",
    answer: "Yes. We use enterprise-grade AES-256 encryption and are pursuing HIPAA compliance. Your data is encrypted at rest and in transit, with granular access controls."
  },
  {
    question: "Can I cancel anytime?",
    answer: "Absolutely. You can cancel your subscription at any time. Your data remains accessible for 30 days after cancellation, and you can export it anytime."
  },
  {
    question: "Do you integrate with my doctor's system?",
    answer: "We're building integrations with major EHR systems. Healthcare providers can access patient data through our provider dashboard with proper permissions."
  },
  {
    question: "What devices do you support?",
    answer: "We support 10+ popular health devices including fitness trackers, glucose monitors, blood pressure monitors, and smart scales. The list grows monthly."
  },
  {
    question: "Is there a free trial?",
    answer: "Yes! Pro and Premium plans include a 30-day free trial. No credit card required. You can downgrade to our free plan anytime."
  },
  {
    question: "How does provider collaboration work?",
    answer: "With your permission, healthcare providers can access your health data, add notes, and track your progress. You maintain full control over what data is shared."
  }
];

interface PricingProps {
  onBackToLanding?: () => void;
  onGetStarted?: () => void;
}

export const Pricing: React.FC<PricingProps> = ({ onBackToLanding, onGetStarted }) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<'individual' | 'provider' | 'enterprise'>('individual');

  const filteredPlans = pricingPlans.filter(plan => 
    selectedUserType === 'individual' ? plan.userType === 'individual' :
    selectedUserType === 'provider' ? ['provider', 'enterprise'].includes(plan.userType) :
    plan.userType === 'enterprise'
  );

  const getYearlyPrice = (monthlyPrice: string) => {
    if (monthlyPrice === '$0' || monthlyPrice === 'Custom') return monthlyPrice;
    const price = parseFloat(monthlyPrice.replace('$', ''));
    const yearlyPrice = price * 12 * 0.8; // 20% discount
    return `$${yearlyPrice.toFixed(0)}`;
  };

  const toggleFaq = (question: string) => {
    setExpandedFaq(expandedFaq === question ? null : question);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/zippy-logo-simple.svg" 
                alt="ZippyHealth Logo"
                className="w-8 h-8"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-800">ZippyHealth.io</h1>
                <p className="text-xs text-slate-500">Pricing & Plans</p>
              </div>
            </div>
            <button 
              onClick={onBackToLanding || (() => window.history.back())}
              className="text-slate-600 hover:text-slate-800 font-medium"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">
            Choose Your <span className="text-red-600">Health Journey</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
            From personal health tracking to enterprise healthcare solutions, 
            we have the right plan for your needs.
          </p>
          
          {/* User Type Selector */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-xl p-2 border border-slate-200">
              <button
                onClick={() => setSelectedUserType('individual')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  selectedUserType === 'individual' 
                    ? 'bg-red-600 text-white' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Heart className="h-4 w-4 inline mr-2" />
                Individuals
              </button>
              <button
                onClick={() => setSelectedUserType('provider')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  selectedUserType === 'provider' 
                    ? 'bg-red-600 text-white' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Users className="h-4 w-4 inline mr-2" />
                Providers
              </button>
              <button
                onClick={() => setSelectedUserType('enterprise')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  selectedUserType === 'enterprise' 
                    ? 'bg-red-600 text-white' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Building className="h-4 w-4 inline mr-2" />
                Enterprise
              </button>
            </div>
          </div>

          {/* Billing Toggle (only for individuals) */}
          {selectedUserType === 'individual' && (
            <div className="flex justify-center items-center space-x-4 mb-8">
              <span className={`${billingPeriod === 'monthly' ? 'text-slate-900' : 'text-slate-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  billingPeriod === 'yearly' ? 'bg-red-600' : 'bg-slate-300'
                }`}
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
              <span className={`${billingPeriod === 'yearly' ? 'text-slate-900' : 'text-slate-500'}`}>
                Yearly
              </span>
              {billingPeriod === 'yearly' && (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-medium">
                  Save 20%
                </span>
              )}
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl border-2 p-8 relative ${
                plan.popular 
                  ? 'border-red-500 shadow-xl scale-105' 
                  : 'border-slate-200 hover:border-slate-300'
              } transition-all duration-300`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-1">
                    <Star className="h-4 w-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-slate-600 mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">
                    {billingPeriod === 'yearly' && selectedUserType === 'individual' 
                      ? getYearlyPrice(plan.price) 
                      : plan.price}
                  </span>
                  {plan.price !== 'Custom' && (
                    <span className="text-slate-600 ml-2">
                      /{billingPeriod === 'yearly' && selectedUserType === 'individual' ? 'year' : plan.period}
                    </span>
                  )}
                </div>
                <button className={`w-full py-3 px-6 rounded-xl font-semibold transition-all ${
                  plan.popular
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}>
                  {plan.cta}
                </button>
              </div>

              <div className="space-y-4">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    {feature.included ? (
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <span className={`${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
                        {feature.name}
                      </span>
                      {feature.limit && (
                        <span className="text-sm text-slate-500 ml-2">({feature.limit})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Security & Compliance */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Enterprise-Grade Security Included
            </h2>
            <p className="text-xl text-slate-600">
              Every plan includes our comprehensive security and compliance features
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 border border-blue-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">HIPAA Compliant*</h3>
              <p className="text-slate-600">Full healthcare privacy compliance with audit trails and data governance.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-purple-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">AES-256 Encryption</h3>
              <p className="text-slate-600">Military-grade encryption for all data at rest and in transit.</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-green-200">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Brain className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">SOC 2 Certified*</h3>
              <p className="text-slate-600">Independently verified security controls and operational standards.</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 text-center mt-6">
            *Certifications pending - we are actively pursuing full compliance
          </p>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-slate-200">
                <button
                  onClick={() => toggleFaq(faq.question)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="text-lg font-semibold text-slate-900">{faq.question}</span>
                  <HelpCircle className={`h-5 w-5 text-slate-500 transition-transform ${
                    expandedFaq === faq.question ? 'rotate-180' : ''
                  }`} />
                </button>
                {expandedFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Sales */}
        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl text-white p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Our enterprise team can create a tailored plan for your organization's specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-red-600 hover:bg-red-50 px-8 py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all">
              <Phone className="h-5 w-5" />
              <span>Schedule Call</span>
            </button>
            <button className="border-2 border-white/30 hover:border-white/50 text-white px-8 py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all">
              <Mail className="h-5 w-5" />
              <span>Contact Sales</span>
            </button>
          </div>
          <p className="text-red-200 text-sm mt-6">
            Enterprise support • Custom integrations • Dedicated success manager
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing; 
